import { AxiosResponse, AxiosResponseOrData } from 'axios';
import { useEffect, useState } from 'react';

type Fetcher<TOut> = (...arg: any[]) => Promise<AxiosResponseOrData<TOut>>;
type InferFetcherOut<T> = T extends Fetcher<infer TOut> ? TOut : never;

/**
 * Type guard permettant de vérifier s'il s'agit d'une réponse axios ou directement
 * des données.
 */
function isAxiosResponse<T>(responseOrData: AxiosResponseOrData<T>): responseOrData is AxiosResponse<T> {
    const r = responseOrData as AxiosResponse<T>;

    if (r && r.status && r.statusText) {
        return true;
    }

    return false;
}

/**
 * Hook utilitaire pour effectuer des requêtes et encapsuler la logique de récupération,
 * chargement, etc...
 *
 * /!\ les paramètres fournis, s'ils changent, entraîne un renouvellement de la requête ce qui
 * signifie que dans le cas d'un objet complexe, il vous faudra passer par un useMemo pour
 * éviter des appels infinis dans le useEffect.
 */
export function useQuery<TFetcher extends Fetcher<TData>, TData = InferFetcherOut<TFetcher>>(
    fetcher: TFetcher,
    ...parameters: Parameters<TFetcher>
) {
    const [data, setData] = useState<TData>();
    const [loading, setLoading] = useState(false);

    function send() {
        setLoading(true);
        fetcher
            .apply(this, parameters)
            .then((r) => setData(isAxiosResponse(r) ? r.data : r))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        send();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, parameters);

    return { data, loading, refresh: send };
}
